import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchCollectionReadiness,
  prepareCollection,
} from "../service/documentCollections";

// Aggregate states the backend reports for a group.
export const COLLECTION_READY = "ready";
export const COLLECTION_PARTIAL = "partial";
export const COLLECTION_PREPARING = "preparing";
export const COLLECTION_NOT_READY = "not_ready";
export const COLLECTION_EMPTY = "empty";

/**
 * A group is chat-able as soon as ONE member is, not once all of them
 * are. Twelve scans finish at twelve different times, and making the
 * attorney wait for the slowest before asking anything would be the
 * wrong trade — the answer is scoped to the members that are ready, and
 * the refusal copy says so when nothing matches.
 */
export const isCollectionChattable = (state) =>
  state === COLLECTION_READY || state === COLLECTION_PARTIAL;

// Keep polling while anything is still moving. `partial` still polls:
// members finish one by one and the count in the UI should climb.
export const isCollectionInFlight = (readiness) => {
  if (!readiness) return false;
  if (readiness.state === COLLECTION_PREPARING) return true;
  return (readiness.in_progress || 0) > 0;
};

/**
 * Track a collection's aggregate AI readiness and expose the group
 * prepare trigger.
 *
 * Mirrors `useDocumentAiReadiness`: no-op without an id, polls only
 * while work is in flight, pauses on a hidden tab, and guards against a
 * slow response for collection A landing after the user switched to B.
 *
 * Returns `{ readiness, loading, preparing, prepareError, prepare,
 * refresh }`. `readiness` is the raw payload (state, total, ready,
 * in_progress, failed, not_ready, prepare_enabled, documents[]) or null
 * before the first fetch.
 */
export default function useCollectionAiReadiness({
  collectionId,
  intervalMs = 6000,
} = {}) {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(Boolean(collectionId));
  const [preparing, setPreparing] = useState(false);
  const [prepareError, setPrepareError] = useState(null);
  const generationRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!collectionId) return null;
    const generation = generationRef.current;
    try {
      const data = await fetchCollectionReadiness(collectionId);
      if (generationRef.current === generation) {
        setReadiness(data);
        setLoading(false);
      }
      return data;
    } catch (error) {
      if (generationRef.current === generation) {
        setLoading(false);
      }
      return null;
    }
  }, [collectionId]);

  useEffect(() => {
    generationRef.current += 1;
    setReadiness(null);
    setPrepareError(null);
    setPreparing(false);
    setLoading(Boolean(collectionId));
  }, [collectionId]);

  const inFlight = isCollectionInFlight(readiness);
  const shouldPoll =
    Boolean(collectionId) && (readiness === null || inFlight);

  useEffect(() => {
    if (!shouldPoll) return undefined;

    let cancelled = false;
    let intervalId = null;

    const poll = () => {
      if (!cancelled) refresh();
    };

    const start = () => {
      if (intervalId !== null) return;
      poll();
      intervalId = setInterval(poll, intervalMs);
    };

    const stop = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const isVisible = () =>
      typeof document === "undefined" ||
      document.visibilityState !== "hidden";

    const handleVisibilityChange = () => {
      if (cancelled) return;
      if (isVisible()) {
        start();
      } else {
        stop();
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    if (isVisible()) {
      start();
    }

    return () => {
      cancelled = true;
      stop();
      if (typeof document !== "undefined") {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      }
    };
  }, [shouldPoll, intervalMs, refresh]);

  const prepare = useCallback(async () => {
    if (!collectionId || preparing) return;
    const generation = generationRef.current;
    setPreparing(true);
    setPrepareError(null);
    try {
      await prepareCollection(collectionId);
      if (generationRef.current !== generation) return;
      await refresh();
    } catch (error) {
      if (generationRef.current !== generation) return;
      setPrepareError(
        error?.response?.data?.error ||
          "Could not start AI preparation. Please try again."
      );
    } finally {
      setPreparing(false);
    }
  }, [collectionId, preparing, refresh]);

  return { readiness, loading, preparing, prepareError, prepare, refresh };
}
